import axios from "axios";
import { getAuth } from "firebase/auth";
import { getApiUrl } from "../../src/utils/envConfig";
import { validateSecurityHeaders, getApiSecurityHeaders } from "../config/securityHeaders";
import { getAuthToken, refreshAuthToken } from "../utils/authUtils";
import { isUnrecoverableAuthFailure } from "../utils/authFailureCodes";
import { extractClientId } from "../utils/extractClientId";
import {
  normalizeExecutionViewFilters,
  buildExecutionViewFilterBody,
} from "./normalizeExecutionViewFilters";
import logger from "../utils/logger";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types).
type SelfChangePasswordResponse = components["schemas"]["SelfChangePasswordResponse"];
type SubbrandVariantsResponse = components["schemas"]["SubbrandVariantsResponse"];

// Enhanced axios utility with token management and retry logic.
export const makeAuthenticatedRequest = async (url: string, options: any = {}, retries = 3) => {
  try {
    // Always ensure we have a valid token before making the request
    const token = await getAuthToken();

    const headers = {
      "content-type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(), // Add security headers
      ...options.headers,
    };

    const requestConfig: any = {
      url,
      headers,
      method: options.method || "GET",
      timeout: options.timeout || 60000, // 60 second timeout by default
      signal: options.signal, // Add cancellation signal support
      ...options,
    };

    // Handle data/body for axios
    if (options.body) {
      requestConfig.data = JSON.parse(options.body);
      delete requestConfig.body;
    }
    const response = await axios(requestConfig);

    // Validate security headers in development/dev
    if (
      import.meta.env.VITE_ENVIRONMENT === "development" ||
      import.meta.env.VITE_ENVIRONMENT === "dev"
    ) {
      validateSecurityHeaders(response);
    }

    return response.data;
  } catch (error: any) {
    // Check if request was cancelled/aborted - don't retry or log as error
    if (
      error.code === "ERR_CANCELED" ||
      error.message === "canceled" ||
      error.name === "AbortError" ||
      (error.response === undefined && error.request === undefined)
    ) {
      // Create a proper AbortError to maintain consistency
      const abortError: any = new Error("Request was aborted");
      abortError.name = "AbortError";
      abortError.code = "ERR_CANCELED";
      throw abortError;
    }

    // Log the actual error for debugging (but not for cancelled requests)
    console.error("API request failed:", {
      url,
      method: options.method || "GET",
      error: error.message,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      code: error.code,
    });

    const status = error.response?.status;

    // A 403 on a scoped request may mean our selected workspace was
    // decommissioned server-side, leaving this tab on a dead id_client.
    // Fire-and-forget a re-reconcile; the 403 still surfaces to the caller for
    // the per-slice access notice. Dynamic import breaks the cycle
    // (workspaceRecovery → store → slices → this module).
    if (status === 403) {
      import("../utils/workspaceRecovery")
        .then(({ recoverFromWorkspaceScoped403 }) => recoverFromWorkspaceScoped403(url))
        .catch(() => {});
    }

    // A revoked or idle-timed-out session is terminal: refreshing returns a
    // token carrying the same `auth_time` the server just rejected, so the
    // retry below would spend the whole budget and leave the dead session in
    // place. Sign out instead, which clears the persisted record.
    if (isUnrecoverableAuthFailure(status, error.response?.data)) {
      const { handleSessionRevoked } = await import("../utils/sessionRevoked");
      await handleSessionRevoked();
      const sessionError: any = new Error("Session ended");
      sessionError.status = status;
      sessionError.code = error.response?.data?.code;
      throw sessionError;
    }

    // 401 = expired/invalid token: refresh once and retry. 403 = authenticated
    // but forbidden (authorization) — a fresh token can't change the verdict,
    // so retrying only multiplies the failures. Fall through and surface it.
    if (status === 401 && retries > 0) {
      try {
        await refreshAuthToken();
        // Retry the request with the new token
        return await makeAuthenticatedRequest(url, options, retries - 1);
      } catch (tokenError: any) {
        const refreshError: any = new Error("Failed to refresh token: " + tokenError.message);
        refreshError.status = status;
        throw refreshError;
      }
    }

    // For exhausted retries, throw with original error details
    if (retries <= 0) {
      const maxRetriesError: any = new Error(
        `Max retries reached. ${error.message || "Could not complete the request."}`
      );
      maxRetriesError.status = status;
      throw maxRetriesError;
    }

    // Re-throw, preserving the HTTP status so callers can distinguish a 403
    // (forbidden — surface an access notice) from a transport failure.
    if (status !== undefined) {
      error.status = status;
    }
    throw error;
  }
};

// Authentication utilities are now imported from ../utils/authUtils

// Mock drinks data
const mockDrinks = {
  coca_cola_regular: {
    availability: 60,
    bundles: 50,
    content: 70,
    name: "Coca-Cola Regular 330ml Can",
    total_dsf_count: 20997,
  },
};

// Shape the payload into the drinks table format
const transformToDrinkData = (data) => {
  const drinks = [
    {
      drink: {
        id: "PET_Performance",
        name: "ONE Performance",
      },
    },
  ];

  const skuIds = Object.keys(data);

  skuIds.forEach((skuId) => {
    const drink = {
      drink: {
        ...data[skuId],
        id: skuId,
      },
    };

    drinks.push(drink);
  });

  return drinks;
};

// waitForUser and getAuthToken are now imported from ../utils/authUtils

// Export outlets data (100k records)
export const exportOutletsData = async ({
  filterOptions,
  sorterOptions,
  searchTerm,
  country,
  id_client,
  company,
  sku_parameters,
}): Promise<{ success: boolean; filename: string }> => {
  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    // Use id_client as fallback if company is empty
    const companyParam = company && company.trim() !== "" ? company : id_client;
    const url = `${getApiUrl()}/metrics/agg_outlet_skus/export?id_client=${id_client}&country=${country}&company=${companyParam}${searchParam}`;

    // filterOptions is already normalized by normalizeExecutionViewFilters (in
    // useExport). Assemble the body via the shared builder so the export sends the
    // exact same filter shape as the data table — dropping empty buckets that
    // would otherwise flip the backend brand-metrics join to INNER and return 0 rows.
    const filterObj = buildExecutionViewFilterBody(filterOptions);

    // Build sort payload
    let sortPayload = null;
    if (sorterOptions && Object.keys(sorterOptions).length > 0) {
      sortPayload = sorterOptions.sort || sorterOptions;
    }

    const bodyPayload: any = {
      ...(Object.keys(filterObj).length > 0 ? { filter: filterObj } : {}),
      ...(sortPayload ? { sort: sortPayload } : {}),
    };

    if (sku_parameters) {
      bodyPayload.sku_parameters = {
        company: sku_parameters.company ?? null,
        brand: sku_parameters.brand ?? null,
        subbrand: sku_parameters.subbrand ?? null,
        packaging_type: sku_parameters.packaging_type ?? null,
        packaging_size: sku_parameters.packaging_size ?? null,
        volume: sku_parameters.volume ?? null,
      };
    }

    const requestBody = bodyPayload;

    const token = await getAuthToken();
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...getApiSecurityHeaders(),
    };

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the blob from response (backend returns file directly, not JSON)
    const blob = await response.blob();

    // Get filename from Content-Disposition header if available
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = `Execution_View_export_${country}_${new Date().toISOString().slice(0, 10)}.csv`;

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
      }
    }

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the blob URL
    window.URL.revokeObjectURL(blobUrl);

    return { success: true, filename };
  } catch (error) {
    console.error("Failed to export outlets data:", error);
    throw error;
  }
};

export const fetchOutletsData = async ({
  page,
  page_size,
  filterOptions,
  sorterOptions,
  searchTerm,
  country,
  id_client,
  company,
  sku_parameters,
}): Promise<any> => {
  try {
    let searchParam = "";
    if (searchTerm && searchTerm.trim() !== "") {
      searchParam = `&search=${encodeURIComponent(searchTerm.trim())}`;
    }

    const url = `${getApiUrl()}/metrics/agg_outlet_skus?id_client=${id_client}&country=${country}&page=${page}&limit=${page_size}&company=${company}${searchParam}`;

    const sortPayload = sorterOptions && sorterOptions.sort ? sorterOptions.sort : sorterOptions;

    // Build filter body via the shared builder (same shape used by the export
    // path) — only non-empty filter categories are included.
    const filterObj: any = buildExecutionViewFilterBody(
      normalizeExecutionViewFilters(filterOptions)
    );

    const bodyPayload: any = {
      ...(Object.keys(filterObj).length > 0 ? { filter: filterObj } : {}),
      ...(sortPayload ? { sort: sortPayload } : {}),
    };

    if (sku_parameters) {
      bodyPayload.sku_parameters = {
        company: sku_parameters.company ?? null,
        brand: sku_parameters.brand ?? null,
        subbrand: sku_parameters.subbrand ?? null,
        packaging_type: sku_parameters.packaging_type ?? null,
        packaging_size: sku_parameters.packaging_size ?? null,
        volume: sku_parameters.volume ?? null,
      };
    }

    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(bodyPayload),
    });

    return data;
  } catch (error) {
    console.error(error);

    throw new Error("Failing fetching outlet data: " + error.message);
  }
};

// Serves the mockDrinks fixture behind a simulated network delay
export const fetchDrinksData = async () => {
  try {
    const data = await new Promise((resolve) => {
      setTimeout(() => {
        resolve(mockDrinks);
      }, 1000);
    });
    return transformToDrinkData(data);
  } catch {
    throw new Error("Failing fetching drink data");
  }
};

export const fetchFilterDimensionsData = async ({ id_client, country }): Promise<any> => {
  try {
    const url = `${getApiUrl()}/filter-dimensions?id_client=${id_client}&country=${country}`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failing fetching filter dimensions: " + error.message);
  }
};

export const fetchFilterDimensionsByCountry = async ({ countries, id_client }): Promise<any> => {
  try {
    const searchParams = new URLSearchParams();
    if (Array.isArray(countries)) {
      countries.forEach((country) => searchParams.append("country", country));
    } else {
      searchParams.append("country", countries);
    }

    // Add id_client parameter
    searchParams.append("id_client", id_client);

    const url = `${getApiUrl()}/filter-dimensions?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failing fetching filter dimensions by countries: " + error.message);
  }
};

export const fetchMarketOverviewMetricFilterDimensions = async ({
  countries,
  id_client,
  refresh_from,
  refresh_to,
  platform,
  segment_type,
  is_part_of_chain,
  franchise_name,
  franchise_only,
}): Promise<any> => {
  try {
    const searchParams = new URLSearchParams();
    searchParams.append("id_client", id_client);

    if (Array.isArray(countries)) {
      countries.forEach((country) => {
        if (country) {
          searchParams.append("country", country);
        }
      });
    } else if (countries) {
      searchParams.append("country", countries);
    }

    if (refresh_from) {
      searchParams.append("refresh_from", String(refresh_from));
    }
    if (refresh_to) {
      searchParams.append("refresh_to", String(refresh_to));
    }
    if (platform && platform !== "Total") {
      searchParams.append("platform", platform);
    }
    if (segment_type && segment_type !== "both") {
      searchParams.append("segment_type", segment_type);
    }
    if (is_part_of_chain === true || is_part_of_chain === false) {
      searchParams.append("is_part_of_chain", String(is_part_of_chain));
    }

    if (Array.isArray(franchise_name)) {
      franchise_name.forEach((name) => {
        if (name && name !== "All Franchises") {
          searchParams.append("franchise_name", name);
        }
      });
    } else if (franchise_name && franchise_name !== "All Franchises") {
      searchParams.append("franchise_name", franchise_name);
    }

    if (franchise_only === true) {
      searchParams.append("franchise_only", "true");
    }

    const url = `${getApiUrl()}/metrics/filter-dimensions?${searchParams.toString()}`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    throw new Error("Failing fetching market overview filter dimensions: " + error.message);
  }
};

// Fetch user data and extract company (id_client) value
export const fetchUserData = async (withScope = true): Promise<any> => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error("User not authenticated");
    }

    const userId = currentUser.uid;
    const url = `${getApiUrl()}/users/${userId}?with_scope=${withScope}`;

    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw Object.assign(new Error("Failed to fetch user data: " + error.message), {
      status: error.status,
    });
  }
};

export const fetchOverviewData = async (
  segmentType,
  platformType,
  existingUserData = null,
  selectedWorkspace = null,
  options: any = {}
): Promise<any> => {
  try {
    let userData, idClient, userId;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      idClient = extractClientId(userData, selectedWorkspace);
      // Prefer the LIVE Firebase uid over the persisted userData.id_user.
      // After an admin delete-and-re-invite the persisted id_user is a dead uid
      // (→ /metrics/v2 403); the current session's uid is the source of truth.
      userId = getAuth().currentUser?.uid ?? userData.id_user;
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      idClient = extractClientId(userData, selectedWorkspace);

      const auth = getAuth();
      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      userId = currentUser.uid;
    }

    let endpoint = `/metrics/v2?id_client=${idClient}&id_user=${userId}`;

    // Add segment_type parameter if specified and not "both"
    if (segmentType && segmentType !== "both") {
      endpoint += `&segment_type=${segmentType}`;
    }

    // Add platform parameter if specified and not "Total"
    if (platformType && platformType !== "Total") {
      endpoint += `&platform=${platformType}`;
    }

    // Add country filter (supports multiple country params)
    if (Array.isArray(options?.countries)) {
      options.countries
        .filter((country) => Boolean(country))
        .forEach((country) => {
          endpoint += `&country=${encodeURIComponent(country)}`;
        });
    } else if (options?.countries) {
      endpoint += `&country=${encodeURIComponent(options.countries)}`;
    }

    // Add refresh range filter
    if (options?.refresh_from) {
      endpoint += `&refresh_from=${encodeURIComponent(String(options.refresh_from))}`;
    }
    if (options?.refresh_to) {
      endpoint += `&refresh_to=${encodeURIComponent(String(options.refresh_to))}`;
    }

    // Period grain; month is the backend default and is not sent
    if (options?.period && options.period !== "month") {
      endpoint += `&period=${encodeURIComponent(String(options.period))}`;
    }

    // Add franchise filter for metrics/v2 when specific values are selected
    const selectedFranchises = Array.isArray(options?.franchise_names)
      ? options.franchise_names
      : Array.isArray(options?.franchise_name)
        ? options.franchise_name
        : [];
    selectedFranchises
      .filter((name) => Boolean(name) && name !== "All Franchises")
      .forEach((name) => {
        endpoint += `&franchise_name=${encodeURIComponent(name)}`;
      });

    // Add chain filter only when explicitly selected as true/false
    if (options?.is_part_of_chain === true || options?.is_part_of_chain === false) {
      endpoint += `&is_part_of_chain=${options.is_part_of_chain}`;
    }

    const url = `${getApiUrl()}${endpoint}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    // For metrics/v2, treat "no data for current filters" as empty state
    // instead of a page-level error.
    if (error?.response?.status === 404) {
      return {
        global_metric: [],
        region_metric: [],
        market_metric: [],
        market_platform_metric: [],
        segment_type: segmentType || "both",
        warnings: ["No data found for the selected filters."],
      };
    }
    throw new Error("Failing fetching overview data: " + error.message);
  }
};

export const createSavedList = async ({
  id_client,
  user_id,
  name,
  id_outlet_info,
  applied_filters,
  selected_skus,
}): Promise<any> => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${user_id}/saved_lists`;

    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify({
        name: name,
        id_outlet_info,
        applied_filters: { ...applied_filters },
        selected_skus,
      }),
    });

    return data;
  } catch (error) {
    throw new Error("Failing create new list: " + error.message);
  }
};

const processGeoscopeData = (geoscopeData) => {
  geoscopeData.forEach((item) => {
    item.title = item.region === "global" ? "Global" : item.region;
    item.value = item.region.toLowerCase();
    item.key = item.region.toLowerCase().replace(/ /g, "_");

    // If item.value is "global", remove its children
    if (item.value === "global" && item.children) {
      item.children = [];
    } else {
      item.children.forEach((child) => {
        if (!child.title) {
          child.title = child.value;
        }
        if (!child.key) {
          const originalValue = child.value.toLowerCase();
          // Make value unique by prefixing with region to avoid collisions
          child.value = `${item.value}_${originalValue}`;
          child.key = `${item.key}_${originalValue.replace(/ /g, "_")}`;
        }
      });
    }
  });

  // Move global item to the first position
  const globalIndex = geoscopeData.findIndex((item) => item.value === "global");
  if (globalIndex > 0) {
    const globalItem = geoscopeData.splice(globalIndex, 1)[0];
    geoscopeData.unshift(globalItem);
  }

  return geoscopeData;
};

// Fetch user client geoscope data
export const fetchUserClientGeoscope = async (
  product_name,
  existingUserData = null,
  selectedWorkspace = null
) => {
  try {
    let userData, id_user, idClient;

    if (existingUserData) {
      // Use existing user data from Redux
      userData = existingUserData;
      // Prefer the LIVE Firebase uid over the persisted userData.id_user.
      // After an admin delete-and-re-invite the persisted id_user is a dead uid
      // (→ /countries 404); the current session's uid is the source of truth.
      id_user = getAuth().currentUser?.uid ?? userData.id_user;
      idClient = extractClientId(userData, selectedWorkspace);
    } else {
      // Fallback to fetching user data
      userData = await fetchUserData(true);
      id_user = userData.id_user;
      idClient = extractClientId(userData, selectedWorkspace);
    }

    if (!idClient) {
      throw new Error("Client ID not found in user data");
    }

    const { regions } = await fetchUserCountries({
      id_client: idClient,
      id_user,
      product_name,
    });

    const geoscopeData = processGeoscopeData(regions);

    return {
      geoscopeData,
      clientId: idClient,
    };
  } catch (error) {
    console.error("Error fetching user client geoscope:", error);
    throw new Error("Failed to fetch user client geoscope: " + error.message);
  }
};

export const GetAllSavedLists = async ({ id_client, user_id }) => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${user_id}/saved_lists`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    logger.error(error);
    throw new Error("Failing get all lists: " + error.message);
  }
};

export const GetListTableData = async ({ id_list, country, id_client, user_id }) => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "latest",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    logger.error(error);
    throw new Error("Failing get list table data: " + error.message);
  }
};

export const GetListRefreshData = async ({ id_list, country, id_client, user_id }) => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "history",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    throw new Error("Failing get list refresh data: " + error.message);
  }
};

export const GetListSummaryData = async ({ id_list, country, id_client, user_id }) => {
  try {
    const searchParams = new URLSearchParams({
      country: country,
      mode: "detail_summary",
      id_client: id_client,
      id_user: user_id,
    });

    const url = `${getApiUrl()}/metrics/${id_list}?${searchParams.toString()}`;
    const data = await makeAuthenticatedRequest(url, { method: "POST" });
    return data;
  } catch (error) {
    throw new Error("Failing get list summary data: " + error.message);
  }
};

export const DeleteList = async ({ id_client, id_user, id_list }) => {
  try {
    const url = `${getApiUrl()}/${id_client}/user/${id_user}/saved_lists/${id_list}`;
    const data = await makeAuthenticatedRequest(url, { method: "DELETE" });
    return data;
  } catch (error) {
    throw new Error("Failing deleting list: " + error.message);
  }
};

type UserCountriesResponse = components["schemas"]["UserCountriesResponse"];

// The countries a user may open for a product, grouped by region, and the
// API's default among them.
export const fetchUserCountries = async ({
  id_client,
  id_user,
  product_name,
}: {
  id_client: string;
  id_user?: string | null;
  product_name?: string;
}): Promise<UserCountriesResponse> => {
  try {
    if (!id_client) {
      throw new Error("Client ID is required");
    }

    const resolvedIdUser = id_user ?? getAuth().currentUser?.uid;
    const resolvedProductName = product_name ?? "execution_view";

    if (!resolvedIdUser) {
      throw new Error("User ID is required");
    }

    const url = `${getApiUrl()}/${id_client}/users/${resolvedIdUser}/countries?product_name=${resolvedProductName}`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    throw new Error("Failed to fetch user countries: " + error.message);
  }
};

export const CreateGoogleUserDoc = async ({ email, id_user }) => {
  try {
    const url = `${getApiUrl()}/users`;

    // Dashmote staff get workspace access from the implicit-access
    // model, not a per-workspace grant. Create an identity-only doc with an
    // empty id_client — the backend defaults default_id_client to ko_global and
    // tolerates the empty map for @dashmote.com staff. (No more hardcoded
    // ko_global / super_admin self-grant on every Google login.)
    const data = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify({
        auth_method: "google",
        email: email,
        id_user: id_user,
        id_client: {},
      }),
    });

    return data;
  } catch (error) {
    throw new Error("Failing creating google user doc: " + error.message);
  }
};

export const fetchSigleOutletsData = async ({ id_outlet, country, id_client }) => {
  try {
    const storeAvailabilityUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/store_availability?country=${country}`;
    const skuListUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/sku_list?country=${country}`;
    const skuInfoUrl = `${getApiUrl()}/client/${id_client}/outlet/${id_outlet}/info?country=${country}`;
    const storeAvailabilityData = await makeAuthenticatedRequest(storeAvailabilityUrl);
    const skuListData = await makeAuthenticatedRequest(skuListUrl);
    const skuInfoData = await makeAuthenticatedRequest(skuInfoUrl);

    return {
      store_availbility: storeAvailabilityData,
      sku_list: skuListData,
      sku_info: skuInfoData,
    };
  } catch (error) {
    throw new Error("Failing fetching single outlet data: " + error.message);
  }
};

export const fetchSKUCompanies = async ({ id_client, country }) => {
  try {
    const companiesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/brands/companies`;
    const companiesListData = await makeAuthenticatedRequest(companiesUrl);
    return companiesListData;
  } catch (error) {
    throw new Error("Failing fetching sku companies: " + error.message);
  }
};

export const fetchSKUBrands = async ({ id_client, company, country }) => {
  try {
    // company is a query param, not a path segment: names like "Carlsberg A/S"
    // contain "/", which would split the path and truncate the value server-side.
    const params = new URLSearchParams({ company });
    const brandsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/brands?${params.toString()}`;
    const brandsListData = await makeAuthenticatedRequest(brandsUrl);
    return brandsListData;
  } catch (error) {
    throw new Error("Failing fetching sku brands: " + error.message);
  }
};

export const fetchSKUSubbrands = async ({ id_client, id_brand, country }) => {
  try {
    const subbrandsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_brand}`;
    const subbrandsListData = await makeAuthenticatedRequest(subbrandsUrl);
    return subbrandsListData;
  } catch (error) {
    throw new Error("Failing fetching sku subbrands: " + error.message);
  }
};

export const fetchSKUDetails = async ({ id_client, id_subbrand, country }) => {
  try {
    const detailsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/details`;
    const detailsListData = await makeAuthenticatedRequest(detailsUrl);
    return detailsListData;
  } catch (error) {
    throw new Error("Failing fetching sku details: " + error.message);
  }
};

export const fetchSKUVolumes = async ({
  id_client,
  id_subbrand,
  country,
  packaging_type,
  packaging_size,
}) => {
  try {
    const params = new URLSearchParams();
    if (packaging_type) {
      params.append("packaging_type", packaging_type);
    }
    if (packaging_size) {
      params.append("packaging_size", packaging_size);
    }
    const queryString = params.toString();
    const volumesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/volumes${
      queryString ? `?${queryString}` : ""
    }`;
    const volumesListData = await makeAuthenticatedRequest(volumesUrl);
    return volumesListData;
  } catch (error) {
    throw new Error("Failing fetching sku volumes: " + error.message);
  }
};

export const fetchSKUPackagingTypes = async ({
  id_client,
  id_subbrand,
  country,
  volume,
  packaging_size,
}) => {
  try {
    const params = new URLSearchParams();
    if (volume) {
      params.append("volume", volume);
    }
    if (packaging_size) {
      params.append("packaging_size", packaging_size);
    }
    const queryString = params.toString();
    const packagingTypesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/packaging-types${
      queryString ? `?${queryString}` : ""
    }`;
    const packagingTypesData = await makeAuthenticatedRequest(packagingTypesUrl);
    return packagingTypesData;
  } catch (error) {
    throw new Error("Failing fetching sku packaging types: " + error.message);
  }
};

export const fetchSKUPackagingSizes = async ({
  id_client,
  id_subbrand,
  country,
  volume,
  packaging_type,
}) => {
  try {
    const params = new URLSearchParams();
    if (volume) {
      params.append("volume", volume);
    }
    if (packaging_type) {
      params.append("packaging_type", packaging_type);
    }
    const queryString = params.toString();
    const packagingSizesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/subbrands/${id_subbrand}/packaging-sizes${
      queryString ? `?${queryString}` : ""
    }`;
    const packagingSizesData = await makeAuthenticatedRequest(packagingSizesUrl);
    return packagingSizesData;
  } catch (error) {
    throw new Error("Failing fetching sku packaging sizes: " + error.message);
  }
};

export const fetchAllSubbrands = async ({ id_client, country, signal }) => {
  try {
    const subbrandsUrl = `${getApiUrl()}/client/${id_client}/country/${country}/brands/subbrands/tree`;
    const subbrandsListData = await makeAuthenticatedRequest(subbrandsUrl, { signal });
    return subbrandsListData;
  } catch (error) {
    if (error?.name === "AbortError" || error?.code === "ERR_CANCELED") throw error;
    throw new Error("Failing fetching all subbrands: " + error.message);
  }
};

// One subbrand node's volume and packaging variants. The subbrand tree stops
// at subbrand level, so the Filters modal loads these when a subbrand opens.
// A node is a subbrand name under a company and brand, so the request names
// all three.
export const fetchSubbrandVariants = async ({
  id_client,
  country,
  company,
  brand,
  subbrand,
}): Promise<SubbrandVariantsResponse> => {
  const params = new URLSearchParams({ company, brand, subbrand });
  const url = `${getApiUrl()}/client/${id_client}/country/${country}/brands/subbrands/variants?${params.toString()}`;
  try {
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    throw new Error("Failing fetching subbrand variants: " + error.message);
  }
};

export const fetchAllDrinksCategories = async ({ id_client, country }) => {
  try {
    const drinksCategoriesUrl = `${getApiUrl()}/client/${id_client}/country/${country}/drink-categories`;
    const drinksCategoriesListData = await makeAuthenticatedRequest(drinksCategoriesUrl);
    return drinksCategoriesListData;
  } catch (error) {
    throw new Error("Failing fetching all drinks categories: " + error.message);
  }
};

// Change the authenticated user's own password.
// Hits the Bearer-authenticated endpoint; the backend derives the target uid
// from the token and clears the force_password_change flag.
export const changeOwnPassword = async ({ newPassword }): Promise<SelfChangePasswordResponse> => {
  try {
    const url = `${getApiUrl()}/users/change-password`;
    const response = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    });
    return response;
  } catch (error) {
    const detail = error.response?.data?.detail || error.message;
    throw new Error(detail);
  }
};

export const forgotPasswordSendEmail = async ({ email }): Promise<any> => {
  try {
    const forgotPasswordSendEmailUrl = `${getApiUrl()}/users/forgot-password`;

    // Use axios directly without authentication since user is not logged in
    const response = await axios({
      method: "POST",
      url: forgotPasswordSendEmailUrl,
      headers: {
        "content-type": "application/json",
        ...getApiSecurityHeaders(),
      },
      data: { email },
    });

    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail || error.message;
    throw new Error(detail);
  }
};

// Validate an activation / reset token against the API. Public, no
// auth — hit on load of the /accept-invite and /reset-password pages. Returns
// { status: "valid" | "expired" | "already_activated" | "invalid", email? }.
// A bad/expired token is a 200 with status in the body, NOT an error — so the
// catch path only fires on transport errors / 503 (transient) / 429.
export const validateInvitation = async ({ token, email, mode = "invite" }): Promise<any> => {
  try {
    const params = new URLSearchParams({ token, email, mode });
    const validateInvitationUrl = `${getApiUrl()}/users/validate-invitation?${params.toString()}`;

    const response = await axios({
      method: "GET",
      url: validateInvitationUrl,
      headers: {
        "content-type": "application/json",
        ...getApiSecurityHeaders(),
      },
    });

    return response.data;
  } catch (error) {
    const httpStatus = error.response?.status;
    if (httpStatus === 503 || httpStatus === 429) {
      const transient: any = new Error("Service temporarily unavailable");
      transient.transient = true;
      throw transient;
    }
    const detail = error.response?.data?.detail || error.message;
    throw new Error(detail);
  }
};

export const forgotPasswordResetPassword = async ({ email, password, token }): Promise<any> => {
  try {
    // Credentials go in the JSON body, never the query string — a URL-borne
    // password leaks into access logs, proxies, browser history, and the
    // Referer header.
    const response = await axios({
      method: "POST",
      url: `${getApiUrl()}/users/set-password`,
      headers: {
        "content-type": "application/json",
        ...getApiSecurityHeaders(),
      },
      data: {
        token,
        email,
        password,
      },
    });

    return response.data;
  } catch (error) {
    const detail = error.response?.data?.detail || error.message;
    throw new Error(detail);
  }
};
